import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { imageProfil, profilx, saveProfileData } from '../../Api';
import Icon from '../../assets/images/edit-icon.svg';
import ImgIcon from '../../assets/images/edit-img-icon.svg';
import './Profil.css';

// Function to sanitize inputs to prevent XSS
function sanitizeInput(input) {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.body.textContent || "";
}

function Profil() {
  const [isEditable, setIsEditable] = useState({
    nama: false,
    email: false,
    no_hp: false,
    password: false,
    alamat: false,
  });

  const [profileData, setProfileData] = useState(profilx);
  const [isImageChanged, setIsImageChanged] = useState(false);

  const handleEditClick = (field) => {
    if (field === 'img_path') {
      document.getElementById('profile-image-input').click();
    } else {
      setIsEditable((prevState) => ({
        ...prevState,
        [field]: true,
      }));
    }
  };

  const handleSave = async () => {
    if (profileData.password && profileData.password.length < 6) {
      Swal.fire({
        icon: 'error',
        title: 'Password too short',
        text: 'Password must be at least 6 characters long.',
      });
      return;
    }
  
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(profileData.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid email format',
        text: 'Please enter a valid email address.',
      });
      return;
    }
  
    // Sanitize inputs before saving
    const sanitizedProfileData = {
      ...profileData,
      nama: sanitizeInput(profileData.nama),
      email: sanitizeInput(profileData.email),
      no_hp: sanitizeInput(profileData.no_hp),
      alamat: sanitizeInput(profileData.alamat),
      password: sanitizeInput(profileData.password), // sanitize password
    };
  
    // Remove password if it's empty or undefined
    if (!sanitizedProfileData.password || sanitizedProfileData.password === "undefined") {
      delete sanitizedProfileData.password;
    }
  
    try {
      const updatedProfileData = { ...sanitizedProfileData };
      if (!isImageChanged) {
        delete updatedProfileData.img_path;
      }
  
      const data = await saveProfileData(updatedProfileData);
  
      let successMessage = data.success;
      if (data.emailChanged || data.passwordChanged) {
        successMessage += '. You will be logged out after clicking OK.';
      }
  
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: successMessage,
      }).then(() => {
        setProfileData(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
  
        if (data.emailChanged || data.passwordChanged) {
          localStorage.clear();
          window.location.href = '/login';
        }
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || 'An unexpected error occurred';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    }
  
    setIsEditable({
      nama: false,
      email: false,
      no_hp: false,
      password: false,
      alamat: false,
    });
    setIsImageChanged(false);
  };
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value); // Sanitize the input
    setProfileData({
      ...profileData,
      [name]: sanitizedValue,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const maxSize = 3 * 1024 * 1024; // 3MB
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];

      if (file.size > maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'File too large',
          text: 'The file must be less than 3MB.',
        });
        return;
      }

      if (!validTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Only JPG, JPEG, and PNG files are allowed.',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({
          ...profileData,
          img_path: reader.result,
        });
        setIsImageChanged(true);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="profilpage">
      <div className="img_wrap">
        <div className="img_profil">
          {profileData.img_path ? (
            <img
              className="avatar"
              src={
                profileData.img_path &&
                profileData.img_path.startsWith('data:image')
                  ? profileData.img_path
                  : `${imageProfil}${profileData.img_path}`
              }
              alt="Profile"
            />
          ) : (
            <div className="placeholder-img">No image</div>
          )}
          <button
            onClick={() => handleEditClick('img_path')}
            className="edit-icon edit-img-icon"
          >
            <img src={ImgIcon} alt="edit" />
          </button>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: 'none' }}
            id="profile-image-input"
          />
        </div>
      </div>

      <div className="profile-details">
        <div className="input-group">
          <label>Name</label>
          <div className="input-with-icon">
            <input
              type="text"
              name="nama"
              value={profileData.nama}
              onChange={handleInputChange}
              disabled={!isEditable.nama}
            />
            <button
              onClick={() => handleEditClick('nama')}
              className="edit-icon"
            >
              <img src={Icon} alt="edit" />
            </button>
          </div>
        </div>
        <div className="input-group">
          <label>Email</label>
          <div className="input-with-icon">
            <input
              type="email"
              name="email"
              value={profileData.email}
              onChange={handleInputChange}
              disabled={!isEditable.email}
            />
            <button
              onClick={() => handleEditClick('email')}
              className="edit-icon"
            >
              <img src={Icon} alt="edit" />
            </button>
          </div>
        </div>
        <div className="input-group">
          <label>Phone Number</label>
          <div className="input-with-icon">
            <input
              type="text"
              name="no_hp"
              value={profileData.no_hp}
              onChange={handleInputChange}
              disabled={!isEditable.no_hp}
            />
            <button
              onClick={() => handleEditClick('no_hp')}
              className="edit-icon"
            >
              <img src={Icon} alt="edit" />
            </button>
          </div>
        </div>
        <div className="input-group">
          <label>Alamat</label>
          <div className="input-with-icon">
            <textarea
              name="alamat"
              value={profileData.alamat || ''}
              onChange={handleInputChange}
              disabled={!isEditable.alamat}
            />
            <button
              onClick={() => handleEditClick('alamat')}
              className="edit-icon"
            >
              <img src={Icon} alt="edit" />
            </button>
          </div>
        </div>
        <div className="input-group">
          <label>Password</label>
          <div className="input-with-icon">
            <input
              type="password"
              name="password"
              value={profileData.password || ''}
              onChange={handleInputChange}
              disabled={!isEditable.password}
              placeholder="Kosongkan jika tidak ingin ganti password"
            />
            <button
              onClick={() => handleEditClick('password')}
              className="edit-icon"
            >
              <img src={Icon} alt="edit" />
            </button>
          </div>
        </div>
        {isEditable.nama ||
        isEditable.email ||
        isEditable.no_hp ||
        isEditable.password ||
        isEditable.alamat ||
        isImageChanged ? (
          <button onClick={handleSave} className="save-button">
            Save Change
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default Profil;
